import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { images } from '../shared/assets';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useSiteSettings } from '../../hooks/useContentful';
import { ContentfulText } from '../Contentful';
import { Icon } from '../shared/Icon';
import { PlanYourTripButton } from '../shared/PlanYourTripButton/PlanYourTripButton';
import LanguageDropdown from '../LanguageDropdown/LanguageDropdown';
import { useTranslation } from '../../i18n';
import './UnifiedNavigation.css';

// Navigation assets - with proper white/dark variants
const navAssets = {
  hamburgerWhite: images.navMenuIcon,
  hamburgerDark: images.navMenuIconDark,
  logoWhite: images.footerLogo, // Using footer logo which is white
  logoDark: images.navLogoDark,
  dividerWhite: images.navDropdownArrow,
  dividerDark: images.navDropdownArrowDark,
  arrowWhite: images.navDropdownArrow,
  arrowDark: images.navDropdownArrowDark
};

export type NavigationVariant = 'transparent' | 'light' | 'dark' | 'faded' | 'invert-faded';

interface NavigationProps {
  variant?: NavigationVariant;
  className?: string;
  destination?: 'atacama' | 'patagonia' | 'iguazu' | 'mendoza' | 'santa-catarina' | null;
  forceWhiteAssets?: boolean;
}

const UnifiedNavigation: React.FC<NavigationProps> = ({
  variant = 'transparent',
  className = '',
  destination = null,
  forceWhiteAssets = false
}) => {
  const navigate = useNavigate();
  const { toggleMenu, overlayState } = useOverlayManager();
  const { scrollState } = useScrollManager();
  const { t } = useTranslation();
  const [isSelfbookReady, setIsSelfbookReady] = useState(false);

  // Fetch site settings from Contentful
  const { data: siteSettingsData } = useSiteSettings();
  const siteSettings = siteSettingsData?.[0]?.fields;

  // Load Selfbook SDK script on component mount
  useEffect(() => {
    // Check if script is already loaded
    if (!document.getElementById('selfbook_jssdk')) {
      const script = document.createElement('script');
      script.id = 'selfbook_jssdk';
      script.async = true;
      script.type = 'text/javascript';
      script.src = 'https://sdk.selfbook.com/selfbook.js?hotelId=54939&apiKey=7rSk9WvOl-p5GZw8A97U8XDhSjm6NGZbK3U';

      document.body.appendChild(script);

      // Most widget scripts auto-initialize and create their own trigger
      // Let's mark as ready after a short delay
      setTimeout(() => {
        setIsSelfbookReady(true);
      }, 2000);
    } else {
      // Script already loaded
      setIsSelfbookReady(true);
    }
  }, []);

  // Determine which assets to use based on variant
  // Don't check scroll state when in overlays (menu or resort nav)
  const isInOverlay = overlayState.isMenuOpen || overlayState.isResortNavOpen;
  const isLightVariant = variant === 'light' || (variant === 'transparent' && scrollState.isScrolled && !isInOverlay);
  const isDarkVariant = variant === 'dark';
  const needsDarkAssets = forceWhiteAssets ? false : (isLightVariant || isDarkVariant);

  // Fix #191 - Property logo should persist when menu is engaged
  const getPropertyLogo = () => {
    if (!destination) return needsDarkAssets ? navAssets.logoDark : navAssets.logoWhite;

    // Always show property logo when we have a destination
    const propertyLogos: Record<string, string> = {
      'atacama': images.logoAtacama,
      'patagonia': images.logoPatagonia,
      'iguazu': needsDarkAssets ? images.logoIguazu : images.logoIguazuWhite,
      'mendoza': needsDarkAssets ? images.logoMendoza : images.logoMendozaWhite,
      'santa-catarina': needsDarkAssets ? images.logoSantaCatarina : images.logoSantaCatarinaWhite
    };
    return propertyLogos[destination] || (needsDarkAssets ? navAssets.logoDark : navAssets.logoWhite);
  };

  const assets = {
    hamburger: needsDarkAssets ? navAssets.hamburgerDark : navAssets.hamburgerWhite,
    logo: getPropertyLogo(),
    divider: needsDarkAssets ? navAssets.dividerDark : navAssets.dividerWhite,
    arrow: needsDarkAssets ? navAssets.arrowDark : navAssets.arrowWhite
  };

  const navigationClasses = [
    'unified-navigation',
    `unified-navigation--${variant}`,
    `unified-navigation--${destination? destination : 'awasi'}`,
    'transition-standard', // Standardized animation class
    scrollState.isScrolled && !isInOverlay ? 'scrolled' : '', // Apply scrolled state only when not in overlay
    className
  ].filter(Boolean).join(' ');

  return (
    <nav
      className={navigationClasses}
      data-destination={destination}
    >
      {/* Left Section - Menu + Language */}
      <div className="unified-nav__left">
        <button
          className="unified-nav__menu-button interactive-hover transition-standard"
          onClick={toggleMenu}
          aria-label={overlayState.isMenuOpen ? t('accessibility.navigation.closeMenu') : t('accessibility.navigation.openMenu')}
        >
          <Icon
            name="menu"
            variant={needsDarkAssets ? 'dark' : 'light'}
            size="custom"
            width={24}
            height={7}
            className="unified-nav__menu-icon"
          />
          <span className="unified-nav__menu-text">
            <ContentfulText
              content={overlayState.isMenuOpen ? siteSettings?.menuCloseText : siteSettings?.menuOpenText}
              fallback={overlayState.isMenuOpen ? 'CLOSE' : 'MENU'}
            />
          </span>
        </button>

        <div className="unified-nav__divider">
          <div className="unified-nav__divider-line" />
        </div>

        <LanguageDropdown variant={needsDarkAssets ? 'light' : 'transparent'} className="unified-nav__language" />
      </div>

      {/* Center Logo */}
      <div
        className={`unified-nav__site-logo unified-nav__site-logo--${destination ? destination : 'awasi'}`}
        role="button"
        tabIndex={0}
        aria-label={String(siteSettings?.logoAriaLabel || t('accessibility.navigation.backToHome'))}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const navigatePath = destination ? `/${destination}/` : '/';
          console.log(`Logo clicked - navigating to ${navigatePath}`);
          navigate(navigatePath);
          // Close menu if it's open
          if (overlayState.isMenuOpen) {
            toggleMenu();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const navigatePath = destination ? `/${destination}/` : '/';
            navigate(navigatePath);
            // Close menu if it's open
            if (overlayState.isMenuOpen) {
              toggleMenu();
            }
          }
        }}
      >
      </div>

      {/* Right Section - Plan Your Trip Button */}
      <div className="unified-nav__right">
        <PlanYourTripButton
          property={destination || 'awasi'}
          onClick={() => {
            if (!isSelfbookReady) {
              navigate('/contact/');
              return;
            }

            // Use the bookNow function from Selfbook SDK
            if (typeof (window as any).bookNow === 'function') {
              (window as any).bookNow();
            } else {
              navigate('/contact/');
            }
          }}
        />
      </div>
    </nav>
  );
};

export default UnifiedNavigation;